import React, { useMemo, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { usePageHeaderStatus } from '../../layouts/PageHeaderExtra';

/** Stati predefiniti: Attivo / Non attivo. Il tono decide il colore della tendina. */
export const STATI_ATTIVO = [
    { value: 'attivo', label: 'Attivo', tono: 'attivo' },
    { value: 'non_attivo', label: 'Non attivo', tono: 'non-attivo' },
];

/**
 * Selettore dello stato (regola SUI in docs/sportid-ui.md, capitolo 8): **sempre un menu a tendina**,
 * perché gli stati possono essere più di due (Attivo, Non attivo, Cancellato, …).
 * Scegliendo una voce lo stato si salva subito: nessuna conferma, si resta nella pagina.
 *
 * - `stati`: [{ value, label, tono }] — tono: 'attivo' (verdino), 'non-attivo' (arancio chiaro),
 *   'cancellato' / 'neutro' (grigio). Le voci con `disabled: true` si vedono ma non si scelgono.
 * - `valore`: lo stato attuale.
 * - `onCambia(valore)`: salva; restituisce `{ error }` se non riesce (la tendina resta com'era e il motivo
 *   si mostra in una finestra) oppure `{ annullato: true }` se ha già gestito lei l'avviso.
 */
export const SelettoreStato = ({ stati = STATI_ATTIVO, valore, onCambia, disabled = false, etichetta = 'Stato' }) => {
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState('');
    const attuale = stati.find(s => s.value === valore);
    const cambia = async (e) => {
        const nuovo = e.target.value;
        if (nuovo === valore) return;
        setInCorso(true);
        const esito = await onCambia(nuovo);
        setInCorso(false);
        if (esito?.error) setErrore(esito.error);
    };
    return (
        <>
            <span className={`sid-stato-select is-${attuale?.tono || 'neutro'}`}>
                <select
                    aria-label={etichetta}
                    value={valore ?? ''}
                    onChange={cambia}
                    disabled={disabled || inCorso}
                >
                    {!attuale && <option value={valore ?? ''} disabled>—</option>}
                    {stati.map(s => (
                        <option key={s.value} value={s.value} disabled={s.disabled}>{s.label}</option>
                    ))}
                </select>
                {inCorso && <span className="sid-stato-select__salva">Salvataggio…</span>}
            </span>
            {/* La finestra si apre sopra la pagina (portale) */}
            <Modal className="fade" show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Stato non cambiato</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button className="btn btn-primary btn-add" onClick={() => setErrore('')}>Chiudi</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

/**
 * Stato accanto al titolo della pagina, come `SelettoreStato`.
 *
 * Due modi:
 * - Attivo / Non attivo: `attivo` (bool) e `onCambia(attivo: bool)`.
 * - Stati liberi: `stati`, `valore` e `onCambia(valore)`.
 * Una bozza mostra «Bozza» e non si cambia da qui (si attiva con «Salva e attiva»).
 */
export const useStatoNelTitolo = ({ visibile = true, attivo, bozza = false, stati, valore, onCambia }) => {
    const onCambiaRef = useRef(onCambia);
    onCambiaRef.current = onCambia;
    const libero = Array.isArray(stati);

    const node = useMemo(() => {
        if (!visibile) return null;
        if (bozza) return <span className="sid-stato-chip is-bozza">Bozza</span>;
        if (libero) {
            return <SelettoreStato stati={stati} valore={valore} onCambia={(v) => onCambiaRef.current(v)} />;
        }
        return (
            <SelettoreStato
                valore={attivo ? 'attivo' : 'non_attivo'}
                onCambia={(v) => onCambiaRef.current(v === 'attivo')}
            />
        );
    }, [visibile, attivo, bozza, libero, stati, valore]);

    usePageHeaderStatus(node);
};

/**
 * Prenotazioni che impediscono di mettere Non attivo un prodotto: da oggi in poi e non annullate né
 * rimborsate. Restituisce il messaggio da mostrare, oppure '' se si può disattivare.
 * `prenotazioni` è l'elenco (o la risposta) di GET /booking/userVenues o /booking/userLessons.
 */
export const motivoPrenotazioniFuture = (prenotazioni, cosa) => {
    const lista = Array.isArray(prenotazioni) ? prenotazioni : prenotazioni?.items || [];
    const oggi = new Date().toISOString().slice(0, 10);
    const future = lista
        .filter(b => String(b.bookingDate || b.startDate || '').slice(0, 10) >= oggi
            && !['CANCELLED', 'REFUNDED'].includes(String(b.status || '').toUpperCase()))
        .sort((a, b) => String(a.bookingDate || a.startDate).localeCompare(String(b.bookingDate || b.startDate)));
    if (future.length === 0) return '';
    const ultima = new Date(future[future.length - 1].bookingDate || future[future.length - 1].startDate).toLocaleDateString('it-IT');
    const quante = future.length === 1 ? 'una prenotazione futura' : `${future.length} prenotazioni future`;
    return `Non puoi mettere ${cosa} Non attivo: ci sono ${quante} (fino al ${ultima}). Potrai disattivarlo quando non ci saranno più prenotazioni dai prossimi giorni in poi.`;
};
